import type { FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { ErrorCode, FormattedError } from '@models/error';

import type { ListAssessmentsQueryVariables } from 'generated/assessment.generated';
import {
  useGetAssessmentQuery,
  useListAssessmentsQuery,
  type InitiateBhAssessmentMutation,
  useInitiateBhAssessmentMutation,
} from 'generated/assessment.generated';
import type { ListAssessmentsInput } from 'generated/types';

type IListAssessments = Pick<ListAssessmentsQueryVariables['input'], 'type'> &
  Partial<ListAssessmentsQueryVariables['input']>;

export const useListAssessments = (
  params: IListAssessments,
  skip?: boolean
) => {
  const skipQuery = skip ?? false;
  const { handleError } = useResponseHandler({
    handlerType: 'boundary',
  });
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id =
    params.vitall_patient_id ?? activeProfile?.vitall_patient_id ?? '';
  const response = useListAssessmentsQuery({
    variables: {
      input: {
        ...params,
        vitall_patient_id,
      },
    },
    skip: !vitall_patient_id || skipQuery,
  });
  const { data, error } = response;
  if (error) {
    handleError(error);
  }
  return { ...response, data: data?.listAssessments.data };
};

export const useGetAssessment = (
  params: { assessment_id: string },
  skip = false
) => {
  const { assessment_id } = params;
  const {
    state: { activeProfile },
  } = useSession();
  const vitall_patient_id = activeProfile?.vitall_patient_id ?? '';
  const patient_id = activeProfile?.patient_id ?? '';

  const { handleError } = useResponseHandler({
    handlerType: 'boundary',
  });

  const response = useGetAssessmentQuery({
    variables: {
      input: {
        assessment_id,
        vitall_patient_id,
      },
      patient_id,
    },
    skip: skip || !vitall_patient_id || !assessment_id || !patient_id,
  });
  const { data, error } = response;
  if (error) {
    handleError(error);
  }
  return { ...response, data };
};

export function useInitiateBHAssessment(): [
  (
    params: ListAssessmentsInput
  ) => Promise<FetchResult<InitiateBhAssessmentMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useInitiateBhAssessmentMutation();
  const loading = baseLoading;
  const { parseError, handleError, showBoundary } = useResponseHandler({
    handlerType: 'boundary',
    snackbarMsgId: 'bhaQrCode',
  });

  async function initiateBha(params: ListAssessmentsInput) {
    const response = await mutate({
      variables: { input: { ...params } },
    });
    if (response.errors) {
      const error = parseError(response.errors);
      if (
        // TODO Better error codes.
        (error?.code === 'FORBIDDEN' || error?.code === '403') &&
        typeof error.message === 'string' &&
        error.message.toLowerCase().includes('consent is required')
      ) {
        // User hasn't consented, throw error to trigger error boundary
        const consentError = new FormattedError(
          ErrorCode.ConsentRequired,
          'Consent is required'
        );
        showBoundary(consentError);
      } else {
        handleError(response.errors);
      }
    }
    return response;
  }
  return [initiateBha, loading];
}
